import { Image } from 'expo-image';
import { View, XStack, YStack } from 'tamagui';

import { formatDateTime } from '@/src/utils/dateFormatter';

import PSBadge from './ui/PSBadge';
import PSIcon from './ui/PSIcon';
import PSText from './ui/PSText';
import MaybeGrayscale from './ui/MaybeGrayscale';
import EventLocation from './EventLocation';

/**
 * TicketListItem — one row in a user's list of bookings (molecule).
 *
 * A recessed `$backgroundSecondary` box: an event image on the left, with the
 * title, date-time and ticket count stacked in a column on the right, then a
 * hairline divider and a pinned-location row spanning the full width below.
 * The image has no fixed height — it stretches (`items="stretch"` on the row)
 * to match whatever height the text column needs, so a 3-line title doesn't
 * leave it floating with dead space beside it.
 *
 * The title is `Body2`/`$semibold` and capped at 3 lines (overflow ellipses —
 * booking titles run long); the date-time is a plain `Body2` line, and the
 * ticket count sits below it in the smaller, muted `Body3`. The location row
 * is a `PSIcon` pin plus an underlined `Body2` line, truncating with an
 * ellipsis so a long address can't push the row wide — hand-rolled instead of
 * `PSLinkButton` because that atom has no `numberOfLines`/`flex` to truncate
 * with.
 *
 * Render-only. Takes the RAW slot ISO string and formats it internally via
 * `formatDateTime` (the shared date helper), per the house rule that
 * components never receive a pre-formatted date string.
 *
 * `past` distinguishes the two states this list shows — a booking for a future
 * slot vs one that's already happened. A past row dims its photo and text (the
 * `$past` opacity rides the image style and the text column separately, NOT the
 * whole row) and floats a neutral "Finished" `PSBadge` over the image's
 * bottom-left, matching EventCard's "Sold out" badge. The dim is applied
 * per-section rather than on the outer box on purpose: the badge is the ONE
 * thing that must stay full-opacity (a dimmed "Finished" tag reads as a rendering
 * bug), and a row-level opacity would cascade into it. The row doesn't know WHY
 * it's past — the screen decides that from the slot date and passes the flag.
 */
// Matches CheckoutEventHeader's row image width. A raw layout number (width
// reads the unpopulated `size` token group, which the linter doesn't guard).
const IMAGE_W = 100;
// 3:4 (width:height) portrait, matching the box's own `aspectRatio` below.
// The `Image` needs this as a REAL pixel number rather than `width/height:
// '100%'` of its parent: when `past` wraps it in `MaybeGrayscale`'s native
// color-matrix view, that view has no size of its own (a plain auto-height
// column flex box), so a percentage/absolute-fill child collapses to 0 — the
// image vanishes and only the "Past" badge floats. A real number is content
// that flows through that wrapper normally, same as `EventCard`.
const IMAGE_H = (IMAGE_W * 4) / 3;

// Dim a past booking's photo + text. Matches EventCard's sold-out image dim
// (0.6). Applied per-section (image style + text column) so the "Finished" badge
// stays full-opacity — see the render.
const PAST_OPACITY = 0.6;

export type ReservationListItemProps = {
  imageUrl?: string;
  title: string;
  /** Raw slot ISO datetime, e.g. "2026-06-23T07:30:00". Formatted internally. */
  slotDatetime: string;
  /** Total seats across the booking's ticket lines. */
  ticketCount: number;
  venueName: string;
  googleMapsUrl: string;
  /** Optional map-open seam — a handler makes the location row tappable. */
  onPress?: () => void;
  past?: boolean;
};

export default function ReservationListItem({
  imageUrl,
  title,
  slotDatetime,
  ticketCount,
  venueName,
  googleMapsUrl,
  onPress,
  past = false,
}: ReservationListItemProps) {
  return (
    <YStack
      gap="$md"
      bg="$backgroundSecondary"
      rounded="$base"
      cursor={onPress ? 'pointer' : undefined}
      onPress={onPress}
      p="$md"
    >
      <XStack gap="$md" items="stretch">
        {/* Rounded corners live on a Tamagui wrapper (rounded="$base" resolves
            the token) with overflow="hidden" clipping the raw expo-image inside
            — the same pattern EventCard / CheckoutEventHeader use, since the
            token can't resolve on the image itself (not a Tamagui component). */}
        <View
          width={IMAGE_W}
          aspectRatio={3 / 4}
          rounded="$base"
          overflow="hidden"
        >
          {/* The `past` dim rides the IMAGE (its style) and the text column
              separately, NOT the whole row — so the "Finished" badge, a sibling
              of the image inside this box, stays at full opacity over the
              dimmed photo. Dimming the row would cascade into the badge too. */}
          <MaybeGrayscale enabled={past}>
            <Image
              source={{ uri: imageUrl }}
              style={{
                width: IMAGE_W,
                height: IMAGE_H,
                opacity: past ? PAST_OPACITY : 1,
              }}
              contentFit="cover"
              transition={200}
            />
          </MaybeGrayscale>
        </View>

        <YStack flex={1} gap="$space.xs" opacity={past ? PAST_OPACITY : 1}>
          <PSText.Body1
            fontWeight="$semibold"
            color="$textDark"
            numberOfLines={3}
          >
            {title}
          </PSText.Body1>
          <PSText.Body2 color="$textDark" numberOfLines={1}>
            {formatDateTime(slotDatetime)}
          </PSText.Body2>
          <PSText.Body2 color="$textDarkMuted" numberOfLines={1}>
            {ticketCount} {ticketCount === 1 ? 'ticket' : 'tickets'}
          </PSText.Body2>
        </YStack>
      </XStack>

      <YStack />
      <View zIndex={10} opacity={past ? PAST_OPACITY : 1}>
        <EventLocation venueName={venueName} googleMapsUrl={googleMapsUrl} />
      </View>
    </YStack>
  );
}
